import React, { useEffect, useRef, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import * as Api from '../../api';
import { FacturasEnabled } from '../../../config';
import { BOLETA, FACTURA } from '../../services/DescargarDocumento';
import LinkDocumento from './LinkDocumento';

const SIN_DOCUMENTOS = 'No hay documentos disponibles';

/** Tolera que el backend responda `{result: {boletas}}` o `{boletas}`. */
const leer = (respuesta, campo) => {
  if (!respuesta) {
    return null;
  }
  if (respuesta.result && typeof respuesta.result[campo] !== 'undefined') {
    return respuesta.result[campo];
  }
  return typeof respuesta[campo] !== 'undefined' ? respuesta[campo] : null;
};

/**
 * Documentos de un contrato: las boletas de sus pagos de mantención (varias,
 * una por cuota) y, si `FacturasEnabled` está encendido, su factura (siempre
 * una o ninguna).
 */
const DocumentosContrato = ({ rut, contrato }) => {
  const [boletas, setBoletas] = useState([]);
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(true);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    const cargar = async () => {
      const [respBoletas, respFactura] = await Promise.all([
        Api.GetBoletasMantencion({ rut: rut, contrato: contrato }),
        FacturasEnabled ? Api.GetFactura({ rut: rut, contrato: contrato }) : null,
      ]);

      if (!montado.current) {
        return;
      }

      const listado = leer(respBoletas, 'boletas');
      setBoletas(Array.isArray(listado) ? listado : []);
      setFactura(leer(respFactura, 'factura') || null);
      setLoading(false);
    };
    cargar();
    return () => {
      montado.current = false;
    };
  }, [rut, contrato]);

  const renderBoletas = () => {
    if (boletas.length === 0) {
      return (
        <tr>
          <td colSpan="2" className="sin-documentos">
            {SIN_DOCUMENTOS}
          </td>
        </tr>
      );
    }
    return boletas.map((boleta, index) => (
      <tr key={boleta.movimiento || index}>
        <td>{boleta.cuota}</td>
        <td>
          <LinkDocumento
            params={{
              tipo: BOLETA,
              contrato: boleta.contrato,
              movimiento: boleta.movimiento,
              cuota: boleta.cuota,
            }}
            nombre={'Boleta Cuota N° ' + boleta.cuota}
          />
        </td>
      </tr>
    ));
  };

  const renderFactura = () => {
    if (!factura) {
      return (
        <tr>
          <td colSpan="2" className="sin-documentos">
            {SIN_DOCUMENTOS}
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td>{factura.contrato}</td>
        <td>
          <LinkDocumento
            params={{
              tipo: FACTURA,
              contrato: factura.contrato,
            }}
            nombre={'Factura Contrato ' + factura.contrato}
          />
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="documentos-loading text-center">
        <BeatLoader sizeUnit={'px'} size={15} color={'#811e6c'} loading={true} />
        <p>Cargando documentos...</p>
      </div>
    );
  }

  return (
    <>
      <h3>BOLETAS DE MANTENCIÓN</h3>
      <table className="table">
        <thead>
          <tr className="header">
            <th scope="col">Cuota</th>
            <th scope="col">Documentos</th>
          </tr>
        </thead>
        <tbody>{renderBoletas()}</tbody>
      </table>
      {FacturasEnabled && (
        <>
          <h3>FACTURA</h3>
          <table className="table">
            <thead>
              <tr className="header">
                <th scope="col">Contrato</th>
                <th scope="col">Documentos</th>
              </tr>
            </thead>
            <tbody>{renderFactura()}</tbody>
          </table>
        </>
      )}
    </>
  );
};

export default DocumentosContrato;
